import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { API, Auth } from "aws-amplify";
import { getLLMProject, listLLMModels } from "../graphql/queries";
import { updateLLMProject } from "../graphql/mutations";
import { useUser } from "../contexts/useUser";
import ServiceGroupCard from "./ServiceGroupCard";
import { useLLM } from "llmasaservice-client";
import Services from "./Services";

interface LLMModel {
  id: string;
  vendor: string;
  name: string;
  host?: string;
  description?: string;
  inputTokenPrice?: number;
  outputTokenPrice?: number;
  performanceRating?: string;
  defaultServiceGroup?: string;
  defaultEnabled?: boolean;
  status: string;
  modelKey?: string;
  modelKeyLatest?: string;
}

interface ServiceManagementProps {
  // No props needed - uses useUser hook
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const getModelKey = (model: LLMModel) => {
  // Use the model ID as the key - simple and reliable
  return model.id;
};

export default function ServiceManagement() {
  const { project, user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectData, setProjectData] = useState<any>(null);
  const [models, setModels] = useState<LLMModel[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [openApiKeyDialog, setOpenApiKeyDialog] = useState(false);
  const [apiKeyVendor, setApiKeyVendor] = useState("");
  const [apiKeyHost, setApiKeyHost] = useState("");
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [apiKeyError, setApiKeyError] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [awsSecretAccessKeyError, setAwsSecretAccessKeyError] = useState("");
  const [hostApiKeys, setHostApiKeys] = useState<
    Record<string, { hasKey: boolean; redactedKey?: string }>
  >({});
  const [testingModels, setTestingModels] = useState<Set<string>>(new Set());
  const [testResults, setTestResults] = useState<Map<string, boolean>>(
    new Map()
  );
  const [showApiKey, setShowApiKey] = useState(false);
  const [showAwsSecret, setShowAwsSecret] = useState(false);
  const [configSaveStatus, setConfigSaveStatus] = useState<
    Record<string, "success" | "error" | null>
  >({});  const [showSuggestDefaultsConfirmation, setShowSuggestDefaultsConfirmation] = useState(false);
  const [hasCheckedForEmptyGroups, setHasCheckedForEmptyGroups] = useState(false);
  const [isAutomaticSuggestion, setIsAutomaticSuggestion] = useState(false);

  let url = "https://chat.llmasaservice.io/";

  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }
  const { send, response, idle } = useLLM({
    project_id: project?.id ?? "",
    url: url,
  });

  useEffect(() => {
    checkAdminAuth();
  }, []);

  useEffect(() => {
    if (project?.id) {
      fetchProjectAndModels();
    }
  }, [project?.id]);

  useEffect(() => {
    // Check for empty model groups after project data is loaded
    if (projectData && models.length > 0) {
      checkForEmptyModelGroups();
    }
  }, [projectData, models]);

  const checkAdminAuth = async () => {
    try {
      const session = await Auth.currentSession();
      const groups = session.getAccessToken().payload["cognito:groups"] || [];
      if (groups.includes("llmasaservice-admin")) {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error("Auth error:", error);
      // Not an admin or auth failed, continue as regular user
      setIsAdmin(false);
    }
  };

  const fetchProjectAndModels = async () => {
    if (!project?.id) return;

    setLoading(true);
    try {
      // Fetch project details
      const projectResult = await API.graphql({
        query: getLLMProject,
        variables: { id: project.id },
      });
      const fetchedProjectData = (projectResult as any).data.getLLMProject;
      setProjectData(fetchedProjectData);

      // Fetch available models
      const modelsResult = await API.graphql({
        query: listLLMModels,
      });

      const modelsData = (modelsResult as any).data.listLLMModels.items;
      // Filter out models that don't have a defaultServiceGroup set
      const validModels = modelsData.filter(
        (model: LLMModel) =>
          model.defaultServiceGroup && model.defaultServiceGroup.trim() !== ""
      );
      setModels(validModels);

      // Fetch API key status for all hosts
      await fetchApiKeyStatus(validModels);
    } catch (err) {
      setError("Failed to load service management data: " + String(err));
    }
    setLoading(false);
  };  const handleSuggestDefaultsClick = () => {
    setIsAutomaticSuggestion(false);
    setShowSuggestDefaultsConfirmation(true);
  };
  const handleConfirmSuggestDefaults = async () => {
    setShowSuggestDefaultsConfirmation(false);
    await handleSuggestDefaults();
  };

  const handleCloseSuggestDefaults = () => {
    setShowSuggestDefaultsConfirmation(false);
    // If this was an automatic suggestion and they closed it, mark as checked
    // so we don't show it again in this session
    if (isAutomaticSuggestion) {
      setHasCheckedForEmptyGroups(true);
    }
  };

  const handleSuggestDefaults = async () => {
    try {
      // Group models by their default service group
      const defaultSelections: { [key: string]: string[] } = {};
      models.forEach((model) => {
        if (model.defaultEnabled && model.defaultServiceGroup) {
          const groupKey = model.defaultServiceGroup.toLowerCase();
          if (!defaultSelections[groupKey]) {
            defaultSelections[groupKey] = [];
          }
          // Use getModelKey function for consistent model key format
          const modelKey = getModelKey(model);
          defaultSelections[groupKey].push(modelKey);
        }
      });

      // Update project with default selections
      const updateInput: any = {
        id: project?.id,
        standardGroupModels: defaultSelections["standard"]?.join("|") || "",
        premiumGroupModels: defaultSelections["premium"]?.join("|") || "",
        reasoningGroupModels: defaultSelections["reasoning"]?.join("|") || "",
        superpremiumGroupModels:
          defaultSelections["superpremium"]?.join("|") || "",
        supersmallGroupModels:
          defaultSelections["supersmall"]?.join("|") || "",
      };


      await API.graphql({
        query: updateLLMProject,
        variables: { input: updateInput },
      });

      // Refresh the data to ensure UI is updated
      await fetchProjectAndModels();
    } catch (err: any) {
      console.error("Suggest defaults error:", err);
      const errorMessage =
        err?.errors?.[0]?.message || err?.message || "Unknown error occurred";
      setError("Failed to suggest defaults: " + errorMessage);
    }
  };
  const handleAutomaticManagementToggle = async (checked: boolean) => {
    console.log("Automatic management toggle:", checked);
    try {
      await API.graphql({
        query: updateLLMProject,
        variables: {
          input: {
            id: project?.id,
            automaticModelManagement: checked,
          },
        },
      });

      // Update local state to reflect the change
      setProjectData((prev: any) => ({
        ...prev,
        automaticModelManagement: checked,
      }));
    } catch (err) {
      setError("Failed to update automatic management setting: " + String(err));
    }
  };
  const handleAdminEditModel = (modelId: string) => {
    // Navigate to admin home page, models tab, with the specific model ID
    navigate(`/adminhome?edit=${modelId}#models`);
  };
  const getFieldNameForGroup = (groupName: string): string => {
    const mapping: { [key: string]: string } = {
      supersmall: "supersmallGroupModels",
      standard: "standardGroupModels",
      premium: "premiumGroupModels",
      reasoning: "reasoningGroupModels",
      superpremium: "superpremiumGroupModels",
    };
    return (
      mapping[groupName.toLowerCase()] ||
      `${groupName.toLowerCase()}GroupModels`
    );
  };
  const handleModelToggle = async (groupName: string, modelKey: string) => {
    try {
      const fieldName = getFieldNameForGroup(groupName);

      const currentModels = projectData?.[fieldName as keyof typeof projectData]
        ? (projectData[fieldName as keyof typeof projectData] as string).split(
            "|"
          )
        : [];

      let updatedModels: string[];
      if (currentModels.includes(modelKey)) {
        // Remove model
        updatedModels = currentModels.filter((key: string) => key !== modelKey);
      } else {
        // Add model
        updatedModels = [...currentModels, modelKey];
      }

      const updateInput = {
        id: project?.id,
        [fieldName]: updatedModels.join("|"),
      };

      await API.graphql({
        query: updateLLMProject,
        variables: { input: updateInput },
      });

      // Update local state
      setProjectData((prev: any) => ({
        ...prev,
        [fieldName]: updatedModels.join("|"),
      }));
    } catch (err) {
      setError("Failed to update model selection: " + String(err));
    }
  };
  const getModelsForGroup = (groupName: string) => {
    return models.filter((model) => {
      const groupMatch =
        model.defaultServiceGroup?.toLowerCase() === groupName.toLowerCase();
      const statusMatch =
        model.status === "active" ||
        model.status === "Active" ||
        model.status === "beingDeprecated" ||
        model.status === "beingdeprecated" ||
        model.status === "reasoning" ||
        model.status === "Reasoning";

      return groupMatch && statusMatch;
    });
  };
  const getSelectedModelsForGroup = (groupName: string) => {
    const fieldName = getFieldNameForGroup(groupName);
    const selectedModelsString = projectData?.[
      fieldName as keyof typeof projectData
    ] as string;
    return selectedModelsString ? selectedModelsString.split("|") : [];
  };

  const handleModelTest = async (model: LLMModel): Promise<boolean> => {
    // Add model to testing set
    setTestingModels((prev) => new Set(prev).add(model.id));

    try {
      const testResponse = await send(
        "1+1=?",
        [],
        [],
        false,
        false,
        "model:" + model.id
      );

      console.log("Model test response:", model.name, testResponse);

      const testPassed =
        typeof testResponse === "string" &&
        (testResponse.toLowerCase().includes("2") ||
          testResponse.toLowerCase().includes("two"));

      setTestResults((prev) => new Map(prev).set(model.id, testPassed));

      return testPassed;
    } catch (error) {
      console.error("Model test failed:", error);
      setTestResults((prev) => new Map(prev).set(model.id, false));
      return false;
    } finally {
      setTestingModels((prev) => {
        const newSet = new Set(prev);
        newSet.delete(model.id);
        return newSet;
      });
    }
  };

  const getUniqueHosts = () => {
    const hosts = new Set(models.map((model) => model.host || "Default"));

    // Return in preferred order - Microsoft maps to Azure
    const hostOrder = [
      "Anthropic",
      "OpenAI",
      "Google",
      "Mistral",
      "Amazon",
      "Microsoft",
      "Azure",
      "Default",
    ];
    const orderedHosts = hostOrder.filter((host) => hosts.has(host));

    // If no ordered hosts found, return all unique hosts (for debugging)
    if (orderedHosts.length === 0) {
      console.log("No ordered hosts found, returning all unique hosts");
      return Array.from(hosts);
    }

    return orderedHosts;
  }; // Get host display name
  const getHostDisplayName = (host: string) => {
    const hostDisplayNames: Record<string, string> = {
      OpenAI: "OpenAI",
      Anthropic: "Anthropic",
      Google: "Google AI",
      Mistral: "Mistral AI",
      Amazon: "AWS Bedrock",
      Microsoft: "Azure OpenAI", // Microsoft host maps to Azure OpenAI
      Azure: "Azure OpenAI",
      Default: "Other",
    };
    // Return the display name if found, otherwise return the host value with proper casing
    return (
      hostDisplayNames[host] ||
      host.charAt(0).toUpperCase() + host.slice(1).toLowerCase()
    );
  };

  // Check if host supports system keys (vs user-only keys)
  const hostSupportsSystemKeys = (host: string) => {
    return ["OpenAI", "Anthropic", "Google", "Mistral"].includes(host);
  };

  // Fetch API key status for all hosts
  const fetchApiKeyStatus = async (validModels: LLMModel[]) => {
    if (!project?.id) return;

    const hosts = new Set(validModels.map((model) => model.host || "Default"));
    const keyStatus: Record<string, { hasKey: boolean; redactedKey?: string }> =
      {};

    try {
      for (const host of Array.from(hosts)) {
        // Use host as serviceId
        const response = await API.post("eventapi", "/getRedactedApiKey", {
          body: {
            projectId: project.id,
            serviceId: host.toLowerCase(),
          },
        });

        keyStatus[host] = {
          hasKey: response.keyFound,
          redactedKey: response.redactedApiKey,
        };
      }

      setHostApiKeys(keyStatus);
    } catch (error) {
      console.error("Failed to fetch API key status:", error);
      // Initialize with empty status so UI still renders
      const emptyStatus: Record<
        string,
        { hasKey: boolean; redactedKey?: string }
      > = {};
      for (const host of Array.from(hosts)) {
        emptyStatus[host] = { hasKey: false };
      }
      setHostApiKeys(emptyStatus);
    }
  };
  const handleOpenApiKeyDialog = (host: string) => {
    setApiKeyHost(host);
    setApiKeyVendor(getHostDisplayName(host));
    setApiKeyValue("");
    setAwsSecretAccessKey("");
    setApiKeyError("");
    setAwsSecretAccessKeyError("");
    setShowApiKey(false);
    setShowAwsSecret(false);
    setOpenApiKeyDialog(true);
  };
  const handleCloseApiKeyDialog = () => {
    setOpenApiKeyDialog(false);
    setApiKeyHost("");
    setApiKeyVendor("");
    setApiKeyValue("");
    setApiKeyError("");
    setAwsSecretAccessKey("");
    setAwsSecretAccessKeyError("");
    setShowApiKey(false);
    setShowAwsSecret(false);
  };
  const handleSaveApiKey = async () => {
    if (!apiKeyHost || !apiKeyValue.trim()) {
      setApiKeyError("API key value is required");
      return;
    }

    // AWS requires both access key and secret access key
    if (
      apiKeyHost === "Amazon" &&
      (!awsSecretAccessKey || !awsSecretAccessKey.trim())
    ) {
      setAwsSecretAccessKeyError("AWS Secret Access Key is required");
      return;
    }

    try {
      const session = await Auth.currentSession();
      const userId = user?.id || session.getAccessToken().payload.sub;

      // Use host as serviceId, format AWS keys as JSON like ServiceForm.tsx
      const apiKeyToSave =
        apiKeyHost === "Amazon"
          ? JSON.stringify({
              aws_access_key_id: apiKeyValue.trim(),
              aws_secret_access_key: awsSecretAccessKey.trim(),
            })
          : apiKeyValue.trim();

      await API.post("eventapi", "/updateApiKey", {
        body: {
          projectId: project?.id,
          userId: userId,
          serviceId: apiKeyHost.toLowerCase(),
          apiKey: apiKeyToSave,
        },
      });

      // Refresh API key status
      await fetchApiKeyStatus(models);
      handleCloseApiKeyDialog();
    } catch (error) {
      console.error("Failed to save API key:", error);
      setApiKeyError("Failed to save API key. Please try again.");
    }
  };

  const handleRevokeApiKey = async (host: string) => {
    if (!project?.id) return;

    try {
      const session = await Auth.currentSession();
      const userId = user?.id || session.getAccessToken().payload.sub;

      // Set empty key to revoke
      await API.post("eventapi", "/updateApiKey", {
        body: {
          projectId: project.id,
          userId: userId,
          serviceId: host.toLowerCase(),
          apiKey: "",
        },
      });

      // Refresh API key status
      await fetchApiKeyStatus(models);
    } catch (error) {
      console.error("Failed to revoke API key:", error);
    }
  };

  const handleUpdateVendorConfig = async (field: string, value: string) => {
    if (!project?.id) return;

    try {
      await API.graphql({
        query: updateLLMProject,
        variables: {
          input: {
            id: project.id,
            [field]: value,
          },
        },
      });

      // Update local state
      setProjectData((prev: any) => ({
        ...prev,
        [field]: value,
      }));

      // Show success feedback briefly
      setConfigSaveStatus((prev) => ({ ...prev, [field]: "success" }));
      setTimeout(() => {
        setConfigSaveStatus((prev) => ({ ...prev, [field]: null }));
      }, 2000);

      console.log(`Successfully updated ${field} to: ${value}`);
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      setError(`Failed to update ${field}: ${error}`);

      // Show error feedback briefly
      setConfigSaveStatus((prev) => ({ ...prev, [field]: "error" }));
      setTimeout(() => {
        setConfigSaveStatus((prev) => ({ ...prev, [field]: null }));
      }, 3000);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSaveAzureConfig = async () => {
    if (!project?.id) return;

    try {
      await API.graphql({
        query: updateLLMProject,
        variables: {
          input: {
            id: project.id,
            azureOpenaiBaseUrl: projectData?.azureOpenaiBaseUrl || null,
            azureOpenaiApiVersion: projectData?.azureOpenaiApiVersion || null,
          },
        },
      });

      // Show success feedback
      setConfigSaveStatus((prev) => ({ ...prev, azureConfig: "success" }));
      setTimeout(() => {
        setConfigSaveStatus((prev) => ({ ...prev, azureConfig: null }));
      }, 3000);

      console.log("Successfully saved Azure configuration");
    } catch (error) {
      console.error("Failed to save Azure configuration:", error);
      setError("Failed to save Azure configuration");

      // Show error feedback
      setConfigSaveStatus((prev) => ({ ...prev, azureConfig: "error" }));
      setTimeout(() => {
        setConfigSaveStatus((prev) => ({ ...prev, azureConfig: null }));
      }, 3000);
    }
  };

  const handleSaveAwsConfig = async () => {
    if (!project?.id) return;

    try {
      await API.graphql({
        query: updateLLMProject,
        variables: {
          input: {
            id: project.id,
            awsBedrockRegion: projectData?.awsBedrockRegion || null,
          },
        },
      });

      // Show success feedback
      setConfigSaveStatus((prev) => ({ ...prev, awsConfig: "success" }));
      setTimeout(() => {
        setConfigSaveStatus((prev) => ({ ...prev, awsConfig: null }));
      }, 3000);

      console.log("Successfully saved AWS configuration");
    } catch (error) {
      console.error("Failed to save AWS configuration:", error);
      setError("Failed to save AWS configuration");

      // Show error feedback
      setConfigSaveStatus((prev) => ({ ...prev, awsConfig: "error" }));
      setTimeout(() => {
        setConfigSaveStatus((prev) => ({ ...prev, awsConfig: null }));
      }, 3000);
    }
  };

  const checkForEmptyModelGroups = () => {
    if (!projectData || hasCheckedForEmptyGroups) return;

    const groups = ['standard', 'premium', 'reasoning', 'superpremium', 'supersmall'];
    let hasAnyModels = false;    // Check if any group has models selected
    for (const group of groups) {
      const selectedModels = getSelectedModelsForGroup(group);
      // Check if there are actually models selected (not just empty strings)
      const hasValidModels = selectedModels.some(model => model && model.trim() !== '');
      if (hasValidModels) {
        hasAnyModels = true;
        break;
      }
    }    // Only show suggestion if:
    // 1. No models are selected in any group
    // 2. There are actually models available to select from
    // 3. This hasn't been shown before in this session
    if (!hasAnyModels && models.length > 0) {
      // Add a small delay to avoid showing immediately on page load
      setTimeout(() => {
        setIsAutomaticSuggestion(true);
        setShowSuggestDefaultsConfirmation(true);
      }, 1000);
    }
    
    setHasCheckedForEmptyGroups(true);
  };  useEffect(() => {
    // Check for empty model groups after project data is loaded
    if (projectData && models.length > 0) {
      checkForEmptyModelGroups();
    }
  }, [projectData, models]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }
  return (
    <Box p={3}>
      {/* Service Management Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Model Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure models, API keys, and settings for each service group
                and vendor.
              </Typography>
            </Box>{" "}
            <Box display="flex" gap={1}>              <FormControlLabel
                control={
                  <Switch
                    checked={projectData?.automaticModelManagement === true}
                    onChange={(event) =>
                      handleAutomaticManagementToggle(event.target.checked)
                    }
                    color="success"
                  />
                }
                label="Use pre-configured models (preferred)"
                labelPlacement="start"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="service management tabs"
          >
            <Tab label="Models (pre-configured)" />
            <Tab label="Keys & Settings" />
            <Tab label="Advanced Services (custom)" />
          </Tabs>
        </Box>{" "}
        {/* Tab Panels */}
        <TabPanel value={currentTab} index={0}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Typography variant="h6">Model Selection</Typography>              <Button
                variant="outlined"
                size="small"
                onClick={handleSuggestDefaultsClick}
              >
                Apply Suggested Defaults
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enable or disable models for each service group. Your selections
              will be used when making AI calls.
            </Typography>

            {/* Service Groups */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ServiceGroupCard
                  title="Standard"
                  description="Reliable, cost-effective models for everyday tasks"
                  models={getModelsForGroup("Standard")}
                  selectedModels={getSelectedModelsForGroup("standard")}
                  onModelToggle={(modelKey: string) =>
                    handleModelToggle("standard", modelKey)
                  }
                  isAdmin={isAdmin}
                  onAdminEditModel={handleAdminEditModel}
                  onModelTest={handleModelTest}
                  testingModels={testingModels}
                  testResults={testResults}
                  hasAmazonApiKey={hostApiKeys["Amazon"]?.hasKey}
                  hasAzureApiKey={hostApiKeys["Microsoft"]?.hasKey}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ServiceGroupCard
                  title="Premium"
                  description="High-performance models for complex reasoning"
                  models={getModelsForGroup("Premium")}
                  selectedModels={getSelectedModelsForGroup("premium")}
                  onModelToggle={(modelKey: string) =>
                    handleModelToggle("premium", modelKey)
                  }
                  isAdmin={isAdmin}
                  onAdminEditModel={handleAdminEditModel}
                  onModelTest={handleModelTest}
                  testingModels={testingModels}
                  testResults={testResults}
                  hasAmazonApiKey={hostApiKeys["Amazon"]?.hasKey}
                  hasAzureApiKey={hostApiKeys["Microsoft"]?.hasKey}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ServiceGroupCard
                  title="Reasoning"
                  description="Advanced reasoning models that show their work"
                  models={getModelsForGroup("Reasoning")}
                  selectedModels={getSelectedModelsForGroup("reasoning")}
                  onModelToggle={(modelKey: string) =>
                    handleModelToggle("reasoning", modelKey)
                  }
                  isAdmin={isAdmin}
                  onAdminEditModel={handleAdminEditModel}
                  onModelTest={handleModelTest}
                  testingModels={testingModels}
                  testResults={testResults}
                  hasAmazonApiKey={hostApiKeys["Amazon"]?.hasKey}
                  hasAzureApiKey={hostApiKeys["Microsoft"]?.hasKey}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ServiceGroupCard
                  title="Super Premium"
                  description="Cutting-edge models for the most demanding tasks"
                  models={getModelsForGroup("Super Premium")}
                  selectedModels={getSelectedModelsForGroup("superpremium")}
                  onModelToggle={(modelKey: string) =>
                    handleModelToggle("superpremium", modelKey)
                  }
                  isAdmin={isAdmin}
                  onAdminEditModel={handleAdminEditModel}
                  onModelTest={handleModelTest}
                  testingModels={testingModels}
                  testResults={testResults}
                  hasAmazonApiKey={hostApiKeys["Amazon"]?.hasKey}
                  hasAzureApiKey={hostApiKeys["Microsoft"]?.hasKey}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ServiceGroupCard
                  title="Super Small"
                  description="Ultra-lightweight models for simple tasks and high-speed responses"
                  models={getModelsForGroup("Super Small")}
                  selectedModels={getSelectedModelsForGroup("supersmall")}
                  onModelToggle={(modelKey: string) =>
                    handleModelToggle("supersmall", modelKey)
                  }
                  isAdmin={isAdmin}
                  onAdminEditModel={handleAdminEditModel}
                  onModelTest={handleModelTest}
                  testingModels={testingModels}
                  testResults={testResults}
                  hasAmazonApiKey={hostApiKeys["Amazon"]?.hasKey}
                  hasAzureApiKey={hostApiKeys["Microsoft"]?.hasKey}
                />
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure API keys and settings for each AI service provider. Each
              host can use either our system keys or your own API keys.
            </Typography>
            {getUniqueHosts().map((host) => {
              const hostKey = hostApiKeys[host];
              const supportsSystemKeys = hostSupportsSystemKeys(host);

              return (
                <Box
                  key={host}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    {getHostDisplayName(host)}
                  </Typography>

                  {supportsSystemKeys ? (
                    // Hosts with system key support (OpenAI, Anthropic, Google, Mistral)
                    <Box>
                      {hostKey?.hasKey ? (
                        <Box>
                          <Typography
                            variant="body2"
                            color="success.main"
                            sx={{ mb: 1 }}
                          >
                            ✓ Using your API key
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Key: {hostKey.redactedKey || "••••••••"}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenApiKeyDialog(host)}
                            >
                              Update
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleRevokeApiKey(host)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Currently using our system key
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenApiKeyDialog(host)}
                          >
                            Add Your API Key
                          </Button>
                        </Box>
                      )}

                      {/* Show message if API key management is not available */}
                      {Object.keys(hostApiKeys).length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, fontStyle: "italic" }}
                        >
                          Currently using our system key
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    // Hosts without system keys (AWS, Azure, Microsoft) - show config fields
                    <Box>
                      {" "}
                      {(host === "Azure" || host === "Microsoft") && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium" }}
                            >
                              Azure OpenAI Configuration
                            </Typography>
                            {(projectData?.azureOpenaiBaseUrl ||
                              projectData?.azureOpenaiApiVersion) && (
                              <Chip
                                label="Configured"
                                size="small"
                                color="success"
                              />
                            )}
                          </Box>{" "}
                          <TextField
                            label="Azure OpenAI Base URL"
                            variant="outlined"
                            size="small"
                            value={projectData?.azureOpenaiBaseUrl || ""}
                            onChange={(e) =>
                              setProjectData((prev: any) => ({
                                ...prev,
                                azureOpenaiBaseUrl: e.target.value,
                              }))
                            }
                            placeholder="https://your-deployment.openai.azure.com/"
                            fullWidth
                            helperText="Your Azure OpenAI endpoint URL"
                          />
                          <TextField
                            label="Azure OpenAI Api Version"
                            placeholder="2025-01-01-preview"
                            variant="outlined"
                            size="small"
                            value={projectData?.azureOpenaiApiVersion || ""}
                            onChange={(e) =>
                              setProjectData((prev: any) => ({
                                ...prev,
                                azureOpenaiApiVersion: e.target.value,
                              }))
                            }
                            fullWidth
                            helperText="THe api version to use for Azure OpenAI requests"
                          />
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleSaveAzureConfig()}
                              disabled={
                                !projectData?.azureOpenaiBaseUrl &&
                                !projectData?.azureOpenaiApiVersion
                              }
                            >
                              Save Azure Settings
                            </Button>
                            {configSaveStatus.azureConfig === "success" && (
                              <Typography
                                variant="caption"
                                color="success.main"
                              >
                                ✓ Settings saved
                              </Typography>
                            )}
                            {configSaveStatus.azureConfig === "error" && (
                              <Typography variant="caption" color="error.main">
                                {" "}
                                Error saving settings
                              </Typography>
                            )}
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenApiKeyDialog(host)}
                            >
                              Add Your API Key
                            </Button>
                            {hostApiKeys[host]?.hasKey && (
                              <>
                                <Chip
                                  label="API Key Added"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevokeApiKey(host)}
                                  color="error"
                                  title="Remove API Key"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      )}
                      {host === "Amazon" && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium" }}
                            >
                              AWS Bedrock Configuration
                            </Typography>
                            {projectData?.awsBedrockRegion && (
                              <Chip
                                label="Configured"
                                size="small"
                                color="success"
                              />
                            )}
                          </Box>{" "}
                          <TextField
                            label="AWS Bedrock Region"
                            variant="outlined"
                            size="small"
                            value={projectData?.awsBedrockRegion || ""}
                            onChange={(e) =>
                              setProjectData((prev: any) => ({
                                ...prev,
                                awsBedrockRegion: e.target.value,
                              }))
                            }
                            placeholder="us-east-1"
                            fullWidth
                            helperText="The AWS region where your Bedrock models are available"
                          />
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleSaveAwsConfig()}
                              disabled={!projectData?.awsBedrockRegion}
                            >
                              Save AWS Settings
                            </Button>
                            {configSaveStatus.awsConfig === "success" && (
                              <Typography
                                variant="caption"
                                color="success.main"
                              >
                                ✓ Settings saved
                              </Typography>
                            )}
                            {configSaveStatus.awsConfig === "error" && (
                              <Typography variant="caption" color="error.main">
                                Error saving settings
                              </Typography>
                            )}
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenApiKeyDialog(host)}
                            >
                              Add Your AWS Keys
                            </Button>
                            {hostApiKeys[host]?.hasKey && (
                              <>
                                <Chip
                                  label="AWS Keys Added"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevokeApiKey(host)}
                                  color="error"
                                  title="Remove AWS Keys"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      )}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Configure your {getHostDisplayName(host)} settings above
                        and save them.
                        {(host === "Azure" || host === "Microsoft") &&
                          " You can also add your own API key for additional control."}
                        {host === "Amazon" &&
                          " You can also add your AWS credentials for additional control."}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}{" "}
          </CardContent>
        </TabPanel>
        <TabPanel value={currentTab} index={2}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Advanced Service Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Full control over individual LLM services, API configurations, and
              custom settings. For power users who need granular control.
            </Typography>
            <Services />
          </CardContent>
        </TabPanel>
      </Card>

      {/* API Key Dialog */}
      <Dialog
        open={openApiKeyDialog}
        onClose={handleCloseApiKeyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{`Add API Key - ${apiKeyVendor}`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {apiKeyHost === "Amazon"
              ? "Enter your AWS credentials for AWS Bedrock. These keys will be securely stored and used to authenticate requests."
              : `Enter your API key for ${apiKeyVendor}. This key will be securely stored and used to authenticate requests.`}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={apiKeyHost === "Amazon" ? "AWS Access Key ID" : "API Key"}
            type={showApiKey ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={apiKeyValue}
            onChange={(e) => setApiKeyValue(e.target.value)}
            error={!!apiKeyError}
            helperText={apiKeyError}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowApiKey(!showApiKey)}
                  edge="end"
                >
                  {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
          />

          {apiKeyHost === "Amazon" && (
            <TextField
              margin="dense"
              label="AWS Secret Access Key"
              type={showAwsSecret ? "text" : "password"}
              fullWidth
              variant="outlined"
              value={awsSecretAccessKey}
              onChange={(e) => setAwsSecretAccessKey(e.target.value)}
              error={!!awsSecretAccessKeyError}
              helperText={awsSecretAccessKeyError}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowAwsSecret(!showAwsSecret)}
                    edge="end"
                  >
                    {showAwsSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApiKeyDialog}>Cancel</Button>
          <Button onClick={handleSaveApiKey} variant="contained">
            Save Key
          </Button>        </DialogActions>
      </Dialog>      {/* Suggest Defaults Confirmation Dialog */}
      <Dialog
        open={showSuggestDefaultsConfirmation}
        onClose={handleCloseSuggestDefaults}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isAutomaticSuggestion ? "Configure Your Models" : "Apply Suggested Defaults"}
        </DialogTitle>
        <DialogContent>
          {isAutomaticSuggestion ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We've noticed you don't have any models selected for your service groups. 
                To help you get started with the latest and best models, we recommend applying our suggested defaults.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This will configure recommended models for each service group (Standard, Premium, Reasoning, Super Premium, and Super Small) 
                based on performance, cost-effectiveness, and reliability.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This will overwrite your current model selections with the recommended default models for each service group. Are you sure you want to continue?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuggestDefaults}>
            {isAutomaticSuggestion ? "Configure Later" : "Cancel"}
          </Button>
          <Button onClick={handleConfirmSuggestDefaults} variant="contained" color="primary">
            Apply Suggested Defaults
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
